import { CampaignSubTypeMapping, isXandrDisplayAdsCampaign } from '@bingads-webui-campaign-react/constants';
import {
  BidStrategyToDevices,
  AgeRangesCampaignToAdInsight,
} from '@bingads-webui-campaign-react/xandr-campaign';
import timeZoneMapper from '@bingads-webui-component-legacy/timezonemapper/index';

const getLocationIds = ({ adGroup, bundleCountries }) => {
  const { locationTargets } = adGroup || {};
  if (locationTargets && locationTargets.currentLevelSettings) {
    const { targetOption, currentLocations } = locationTargets.currentLevelSettings;

    if (targetOption === 'bundle' && bundleCountries && bundleCountries.length > 0) {
      return bundleCountries.map(c => c.LocationId);
    }

    if (targetOption === 'specific' && currentLocations && currentLocations.length > 0) {
      return currentLocations.reduce((locationIds, cl) => {
        if (cl.SelectionStatus === 'Included' && cl.CriterionType !== 'Radius') {
          locationIds.push(cl.Criterion.LocationCriterion.LocationId);
        }
        return locationIds;
      }, []);
    }

    return [];
  }

  return [];
};

const getRadiusCriterions = ({ adGroup }) => {
  const { locationTargets } = adGroup || {};
  if (locationTargets && locationTargets.currentLevelSettings) {
    const { currentLocations } = locationTargets.currentLevelSettings;

    if (currentLocations && currentLocations.length > 0) {
      return currentLocations.reduce((radiusTargets, cl) => {
        if (cl.CriterionType === 'Radius') {
          radiusTargets.push({
            CenterLatitude: cl.Criterion.RadiusCriterion.Latitude,
            CenterLongitude: cl.Criterion.RadiusCriterion.Longitude,
            Radius: cl.Criterion.RadiusCriterion.Radius,
          });
        }
        return radiusTargets;
      }, []);
    }

    return [];
  }

  return [];
};

const getExcludedLocationIds = ({ adGroup }) => {
  const { locationTargets } = adGroup || {};
  if (locationTargets && locationTargets.currentLevelSettings) {
    const { targetOption, currentLocations } = locationTargets.currentLevelSettings;

    if (targetOption === 'specific' && currentLocations && currentLocations.length > 0) {
      return currentLocations.reduce((locationIds, cl) => {
        if (cl.SelectionStatus === 'Excluded' && cl.CriterionType !== 'Radius') {
          locationIds.push(cl.Criterion.LocationCriterion.LocationId);
        }
        return locationIds;
      }, []);
    }

    return [];
  }

  return [];
};

const getAudienceCriterionIds = ({ adGroup, key, status }) => {
  if (adGroup && Array.isArray(adGroup[key])) {
    return adGroup[key].reduce((audienceIds, audience) => {
      if (audience.Status === status) {
        audienceIds.push(audience.Criterion.AudienceId);
      }
      return audienceIds;
    }, []);
  }
  return [];
};

export const getInterestIds = ({ adGroup }) => getAudienceCriterionIds({ adGroup, key: 'interests', status: 'Active' });

export const getExcludedInterestIds = ({ adGroup }) => getAudienceCriterionIds({ adGroup, key: 'interests', status: 'Excluded' });

export const getOtherAudienceIds = ({ adGroup }) => getAudienceCriterionIds({ adGroup, key: 'audiences', status: 'Active' });

export const getExcludedOtherAudienceIds = ({ adGroup }) => getAudienceCriterionIds({ adGroup, key: 'audiences', status: 'Excluded' });

export const getAudienceIds = ({ adGroup }) => [...getInterestIds({ adGroup }), ...getOtherAudienceIds({ adGroup })];

export const getExcludedAudienceIds = ({ adGroup }) => [...getExcludedInterestIds({ adGroup }), ...getExcludedOtherAudienceIds({ adGroup })];

const getAgeRanges = ({ adGroup }) => adGroup.ageRanges && adGroup.ageRanges.map(ar => AgeRangesCampaignToAdInsight[ar]);

const getDevices = ({ adGroup, campaignBiddingScheme }) => {
  if (adGroup.dealDetails) {
    return adGroup.deviceTypes;
  }

  return BidStrategyToDevices[campaignBiddingScheme.Type];
};

const getAdSchedule = ({ adGroup }) => {
  if (adGroup && adGroup.adSchedulesBulkUpsert && adGroup.adSchedulesBulkUpsert.length > 0) {
    return adGroup.adSchedulesBulkUpsert.map((a) => {
      const { Day, FromHour, ToHour } = a.Criterion.DayTimeCriterion;
      return {
        Day,
        StartHour: FromHour,
        EndHour: ToHour,
      };
    });
  }
  return [];
};

export const formatReachEstimateData = ({
  adGroup,
  campaignSubtype,
  campaignBiddingScheme,
  bundleCountries,
}) => {
  const reachEstimationTargets = {
    Age: {
      Includes: getAgeRanges({ adGroup }),
    },
    Gender: {
      Includes: adGroup.genderTypes,
    },
    Device: {
      Includes: getDevices({ adGroup, campaignBiddingScheme }),
    },
    Location: {
      Includes: getLocationIds({ adGroup, bundleCountries }),
      Excludes: getExcludedLocationIds({ adGroup }),
    },
    RadiusTargets: getRadiusCriterions({ adGroup }),
    Audience: {
      Includes: getAudienceIds({ adGroup }),
      Excludes: getExcludedAudienceIds({ adGroup }),
    },
    Day: getAdSchedule({ adGroup }),
    TimeZone: timeZoneMapper.getAccountIANATimeZone(),
    CampaignSubType: isXandrDisplayAdsCampaign(campaignSubtype) ? CampaignSubTypeMapping.DisplayAds : CampaignSubTypeMapping.BrandAwarenessVideoAds,
  };

  return reachEstimationTargets;
};

export const areCriterionTargetsSet = (targets, negativeTargets) => (targets && targets.length > 0) || (negativeTargets && negativeTargets.length > 0);

const isTargetSet = (targetSetting, isEditFlow) => !isEditFlow || targetSetting;

export const getTargetSettingsFromCriterions = (adGroupData, isEditFlow) => {
  // target settings are the non empty criterions
  const targetSettings = [];

  if (isTargetSet(adGroupData.isTargetSetCompanyName, isEditFlow) && areCriterionTargetsSet(adGroupData.companyNameTargets, adGroupData.negativeCompanyNameTargets)) {
    targetSettings.push('CompanyName');
  }

  if (isTargetSet(adGroupData.isTargetSetJobFunction, isEditFlow) && areCriterionTargetsSet(adGroupData.jobTargets, adGroupData.negativeJobTargets)) {
    targetSettings.push('JobFunction');
  }

  if (isTargetSet(adGroupData.isTargetSetIndustry, isEditFlow) && areCriterionTargetsSet(adGroupData.industry, adGroupData.negativeIndustry)) {
    targetSettings.push('Industry');
  }

  if (isTargetSet(adGroupData.isTargetSetAudience, isEditFlow) && (areCriterionTargetsSet(adGroupData.audiences, null) || areCriterionTargetsSet(adGroupData.interests, null))) {
    targetSettings.push('Audience');
  }

  // we always add age and gender, the user can either select all age/gender or select only some, it cannot be empty.
  targetSettings.push('Age');
  targetSettings.push('Gender');

  return targetSettings.length > 0 ? targetSettings.join(';') : 'delete_value';
};
